import React from "react";
import PageShell from "../components/layout/PageShell";
import CinematicHero, { HeroOverlay } from "../components/layout/CinematicHero";
import Container from "../components/primitives/Container";
import Section from "../components/primitives/Section";
import Eyebrow from "../components/primitives/Eyebrow";
import Reveal from "../components/primitives/Reveal";
import WorldMap from "../components/editorial/WorldMap";
import SeoHead from "../components/seo/SeoHead";
import { IMAGES } from "../content/images";
import { GLOBAL } from "../content/site";

const GlobalPresence = () => {
  return (
    <PageShell>
      <SeoHead
        title="Global Presence"
        description="Headquartered in the United States, with strategic presence in Luxembourg and Dubai — a deliberately compact international footprint."
        path="/global-presence"
        ogImage={IMAGES.globalSkyline}
      />
      <CinematicHero
        image={IMAGES.globalSkyline}
        imageAlt="A skyline at twilight — the institution's international horizon."
        eager
        data-testid="global-hero"
      >
        <HeroOverlay
          eyebrow={GLOBAL.hero.eyebrow}
          title={GLOBAL.hero.headline}
          statement={GLOBAL.intro}
        />
      </CinematicHero>

      {/* Statement + Map — single continuous block, matching Home rhythm */}
      <Section tone="light" className="pt-16 md:pt-20 lg:pt-24 pb-16 md:pb-20 lg:pb-24" data-testid="global-intro">
        <Container>
          <Reveal className="max-w-3xl">
            <Eyebrow tone="gold">Global Reach</Eyebrow>
            <p
              className="mt-10 font-serif font-normal text-navy text-balance"
              style={{ fontSize: "clamp(28px, 3.4vw, 44px)", lineHeight: 1.25 }}
            >
              Headquartered in the United States, with strategic presence across Europe and the Middle East.
            </p>
          </Reveal>
          <Reveal className="mt-6" delay={120} data-testid="global-map">
            <WorldMap />
          </Reveal>
        </Container>
      </Section>

      {/* Locations */}
      <Section tone="light" className="py-16 md:py-20 lg:py-24" data-testid="global-locations">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-20">
            {GLOBAL.locations.map((loc, i) => (
              <Reveal key={loc.name} delay={i * 80}>
                <div className="flex flex-col">
                  <span aria-hidden="true" className="block h-px w-10 bg-gold" />
                  <span
                    className="mt-5 font-sans uppercase text-gold"
                    style={{ fontSize: "11px", letterSpacing: "0.22em" }}
                  >
                    {loc.eyebrow}
                  </span>
                  <h3 className="mt-4 font-serif text-3xl sm:text-4xl text-navy">{loc.name}</h3>
                  <p className="mt-5 font-sans text-[15px] leading-[1.7] text-navy/70 max-w-[36ch]">
                    {loc.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>

      {/* Region strip */}
      <section
        data-testid="global-regions"
        className="bg-[var(--q-bg-dark)] text-offwhite py-20 md:py-28"
       data-section-tone="dark">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-20">
            {GLOBAL.regions.map((r, i) => (
              <Reveal key={r.eyebrow} delay={i * 80}>
                <div className="flex flex-col">
                  <span aria-hidden="true" className="block h-px w-10 bg-gold" />
                  <span
                    className="mt-5 font-sans uppercase text-gold"
                    style={{ fontSize: "11px", letterSpacing: "0.22em" }}
                  >
                    Region
                  </span>
                  <h3 className="mt-4 font-serif text-2xl sm:text-3xl text-offwhite">{r.eyebrow}</h3>
                  <p className="mt-5 font-sans text-[15px] leading-[1.7] text-offwhite/70 max-w-[36ch]">
                    {r.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
    </PageShell>
  );
};

export default GlobalPresence;
